import { Alert, AlertDescription, AlertIcon, Box, Center, Spinner, Text, VStack } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { AllCommunityModule, ModuleRegistry, themeQuartz, type CellSelectionOptions, type ColDef } from 'ag-grid-community';
import { ClipboardModule } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { zipObject } from 'lodash';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { LabPageHeader } from '../components/lab-page-header';
import { useLabLayoutContext } from '../lab-layout';
import { API_URL, getAuthHeaders } from '../../../utils/fetch';

import './smartchip-run-cts-table-page.css';

ModuleRegistry.registerModules([AllCommunityModule, ClipboardModule]);

const gridThemeClass = 'smartchip-run-cts-table-grid';

type CtsTableRow = Record<string, string>;

type ParsedTable = {
  headers: string[];
  rowData: CtsTableRow[];
};

const BOM = /^(?:\uFEFF)/;

function parseCtsTableContent(raw: string): ParsedTable {
  const normalized = raw.replace(BOM, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalized
    .split('\n')
    .map(line => line.trimEnd())
    .filter(line => line.length > 0);

  if (lines.length === 0) {
    return { headers: [], rowData: [] };
  }

  const headers = lines[0]
    .split('\t')
    .map(header => header.trim())
    .filter(header => header.length > 0);

  const rowData = lines.slice(1).map(line => {
    const values = line.split('\t');
    const normalizedValues =
      values.length >= headers.length
        ? values.slice(0, headers.length)
        : [...values, ...Array.from({ length: headers.length - values.length }, () => '')];

    return zipObject(headers, normalizedValues.map(value => value.trim())) as CtsTableRow;
  });

  return { headers, rowData };
}

async function fetchCtsFileContent(workbookId: string, chipId: number): Promise<string> {
  const response = await fetch(`${API_URL}/lab/${encodeURIComponent(workbookId)}/runs/files/${chipId}/cts`, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const text = await response.text();
    const message = text.trim().length > 0 ? text : `Failed to load CTS file (${response.status}).`;
    throw new Error(message);
  }

  return await response.text();
}

export function SmartChipRunCtsTablePage() {
  const { workbookId, workbookQuery } = useLabLayoutContext();
  const { chipId: chipIdParam } = useParams<{ chipId: string }>();
  const chipId = Number(chipIdParam);

  const chipName = useMemo(() => {
    if (!Number.isFinite(chipId)) {
      return null;
    }
    return workbookQuery.data?.smartChip?.chips.find(chip => chip.id === chipId)?.name ?? null;
  }, [chipId, workbookQuery.data?.smartChip?.chips]);

  const ctsQuery = useQuery({
    queryKey: ['smartchipRunCtsFile', workbookId, chipId],
    queryFn: async () => {
      if (!workbookId || !Number.isFinite(chipId)) {
        throw new Error('Missing workbook or chip identifier.');
      }
      return await fetchCtsFileContent(workbookId, chipId);
    },
    enabled: Boolean(workbookId) && Number.isFinite(chipId),
    staleTime: 30_000,
  });

  const parsedTable = useMemo(() => (ctsQuery.data ? parseCtsTableContent(ctsQuery.data) : null), [ctsQuery.data]);

  const defaultColDef = useMemo<ColDef<CtsTableRow>>(
    () => ({
      editable: false,
      resizable: true,
      sortable: true,
      filter: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      minWidth: 140,
    }),
    [],
  );

  const cellSelection = useMemo<CellSelectionOptions<CtsTableRow>>(
    () => ({
      handle: {
        mode: 'range',
      },
    }),
    [],
  );

  const columnDefs = useMemo<Array<ColDef<CtsTableRow>>>(() => {
    if (!parsedTable) {
      return [];
    }

    return parsedTable.headers.map(header => ({
      headerName: header,
      field: header,
    }));
  }, [parsedTable]);

  return (
    <VStack align="stretch" spacing={6} width="100%">
      <LabPageHeader
        title="CTS table"
        subtitle={
          chipName ? (
            <>
              Chip: <b>{chipName}</b>
            </>
          ) : null
        }
      />

      {!Number.isFinite(chipId) ? (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertDescription>Invalid chip identifier.</AlertDescription>
        </Alert>
      ) : ctsQuery.isLoading ? (
        <Center padding={8}>
          <Spinner />
        </Center>
      ) : ctsQuery.isError ? (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            {ctsQuery.error instanceof Error ? ctsQuery.error.message : 'Failed to load CTS file.'}
          </AlertDescription>
        </Alert>
      ) : !parsedTable ? (
        <Text color="gray.600">No CTS data available.</Text>
      ) : (
        <Box borderRadius={8} border="1px solid" borderColor="gray.200" overflow="hidden" width="100%">
          <div style={{ width: '100%', height: '75vh' }} className={gridThemeClass}>
            <AgGridReact<CtsTableRow>
              theme={themeQuartz}
              rowData={parsedTable.rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowHeight={25}
              cellSelection={cellSelection}
            />
          </div>
        </Box>
      )}
    </VStack>
  );
}
